'use client';

import type React from 'react';
import { useEffect, useState } from 'react';
import { getCookie, setCookie } from 'cookies-next';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { useApp } from '@/forApp/states/appState/appState';
import { useUser } from '@/forApp/states/userState/userState';
import fetcher from '@/forApp/utils/fetcher/fetcher';
import Button from '@/shared/Buttons/Button';
import Input from '@/shared/Input/input';

import styles from './styles.module.css';

interface LoginFormData {
  email: string;
  password: string;
  device_name: string;
}

// Helpers to generate a readable, unique, and safe device name
const getBrowserName = () => {
  const agent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (agent.indexOf('Chrome') > -1 && agent.indexOf('Edg') === -1)
    return 'Chrome';
  if (agent.indexOf('Firefox') > -1) return 'Firefox';
  if (agent.indexOf('Safari') > -1 && agent.indexOf('Chrome') === -1)
    return 'Safari';
  if (agent.indexOf('Edg') > -1 || agent.indexOf('Edge') > -1) return 'Edge';
  return 'Unknown';
};

const generateRandomId = () => Math.random().toString(36).substr(2, 9);

const generateDeviceName = () => {
  const browser = getBrowserName();
  const timestamp = Date.now();
  return `${browser}_${timestamp}_${generateRandomId()}`;
};

type LoginT = {
  setMode: (mode: string) => void;
};

const Login: React.FC<LoginT> = ({ setMode }) => {
  const { delModal, addNotification } = useApp();
  const { getUser } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<LoginFormData>({
    defaultValues: {
      email: '',
      password: '',
      device_name: (getCookie('device_name') as string) || '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    try {
      const cookieDevice = (getCookie('device_name') as string) || '';
      let stored = '';
      if (typeof window !== 'undefined') {
        stored = window.localStorage?.getItem('device_name') || '';
      }

      let finalName = cookieDevice || stored;

      if (!finalName) {
        finalName = generateDeviceName();
        // persist in cookie for 1 year
        setCookie('device_name', finalName, {
          maxAge: 60 * 60 * 24 * 365,
          path: '/',
        });
        try {
          if (typeof window !== 'undefined') {
            window.localStorage?.setItem('device_name', finalName);
          }
        } catch {}
      } else {
        // if only one of the storages has it, sync both
        if (!cookieDevice)
          setCookie('device_name', finalName, {
            maxAge: 60 * 60 * 24 * 365,
            path: '/',
          });
        try {
          if (typeof window !== 'undefined' && !stored) {
            window.localStorage?.setItem('device_name', finalName);
          }
        } catch {}
      }

      // Update form value so it is sent with the request
      setValue('device_name', finalName, {
        shouldValidate: true,
        shouldDirty: false,
      });
    } catch {}
  }, [setValue]);

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);

    try {
      const response = await fetcher<{ token: string }>('POST', '/login', {
        body: JSON.stringify(data),
      });

      if (response.data.token) {
        // Store token in cookie
        setCookie('cg-token', response.data.token);

        // Show success notification
        addNotification({
          text: 'Вы успешно авторизованы',
          type: 'success',
        });
        await getUser();
        // Close modal and redirect
        await delModal();
        router.push('/profile');
      }
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginForm}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.formField}>
          <Input
            name="email"
            control={control}
            type="email"
            placeholder="E-mail"
            rules={{
              required: 'Email обязателен',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Некорректный email',
              },
            }}
            autocomplete="email"
          />
        </div>

        <div className={styles.formField}>
          <Input
            name="password"
            control={control}
            type="password"
            placeholder="Пароль"
            rules={{ required: 'Пароль обязателен' }}
            autocomplete="current-password"
          />
        </div>

        <div className={styles.btnWrapper}>
          <Button
            type="submit"
            text={isLoading ? 'Отправка...' : 'Войти'}
            disabled={!isValid || isLoading}
            isLoading={isLoading}
          />
        </div>

        <div className={styles.btnResetPassword}>
          <Button
            text="Забыли пароль?"
            color="transparent"
            onClick={() => {
              setMode('forgotPassword');
            }}
            className={styles.btnForgot}
          />
        </div>
      </form>
    </div>
  );
};

export default Login;
